<template>
    <div id="client-review">
        <div class="panel">
            <div class="panel-head">
                <h2>Client Review</h2>
            </div>

            <div class="panel-content">
                <div id="settings">
                    <div class="row">
                        <div class="col col-2x">
                            <form>
                                <div class="form-control-group">
                                    <div class="form-control">
                                        <label for="startDate">Start Date</label>
                                        <datepicker id="startDate" :format="formatDate" :value="startDate" @input="setStartDate($event)" required></datepicker>
                                    </div>
                                    <div class="form-control">
                                        <label for="endDate">End Date</label>
                                        <datepicker id="endDate" :format="formatDate" :value="endDate" @input="setEndDate($event)" required></datepicker>
                                    </div>
                                </div>

                                <div class="form-control">
                                    <label for="comparison">Comparison</label>
                                    <select v-model="comparison" name="comparison" id="comparison">
                                        <option value="none">--</option>
                                        <option value="year">Previous year</option>
                                        <option value="period">Previous period</option>
                                    </select>
                                </div>

                                <div class="form-control" v-if="comparison == 'none'">
                                    <label><input type="checkbox" v-model="showCombinedTotals"> Show Combined Totals</label>
                                </div>

                                <div class="form-control" v-if="showCombinedTotals == true">
                                    <label><input type="checkbox" :value="management" @change="setManagement($event.target.checked)"> Include Management Fee</label>
                                </div>

                                <button @click.prevent="updateData()" class="button button-orange">Generate Report</button>
                            </form>
                        </div>

                        <div class="col">

                            <div v-if="selectMultiple">
                                <span>Select Clients</span>
                                <ul class="client-selection">
                                    <li v-for="list in clientLists" >
                                        <label><input type="checkbox" :value="list.id" @change="updateListIds({ add: $event.target.checked, listId: list.id})" :checked="listIds.indexOf(list.id) > -1"> <strong>List:</strong> {{ list.name }}</label>
                                    </li>
                                    <li v-for="client in clients">
                                        <label><input type="checkbox" :value="client.id" @change="updateClientIds({ add: $event.target.checked, clientId: client.id})" :checked="clientIds.indexOf(client.id) > -1"> {{ client.name }}</label>
                                    </li>
                                </ul>
                                <router-link class="client-list-edit" :to="{ name: 'ListIndex' }">add / remove client lists</router-link>
                            </div>

                            <div v-else>
                                <div class="form-control">
                                    <label>Client</label>
                                    <select v-model="clientId">
                                        <option v-for="client in clients"  :value="client.id">{{ client.name }}</option>
                                    </select>
                                </div>

                                <button @click="selectMultiple = true" class="button button-blue button-small">Select Multiple</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-show="noReportData" class="no-data-message">
                    <p><em>No data available matching these parameters.</em></p>
                </div>

                <div v-show="reportData.length > 0" id="charts">
                    <p class="chart-title">Cost Per Lead</p>
                    <canvas ref="costPerLead" height="80"></canvas>

                    <p class="chart-title">Total Leads</p>
                    <canvas ref="totalLeads" height="80"></canvas>

                    <p class="chart-title">Total Investment</p>
                    <canvas ref="totalInvestment" height="80"></canvas>
                </div>

                <table v-if="reportData.length > 0">
                    <thead>
                        <tr>
                            <th>Year - Month</th>
                            <th>Calls</th>
                            <th>Call Leads</th>
                            <th>Call Lead %</th>
                            <th>Form Leads</th>
                            <th>Total Leads</th>
                            <th>Total Spent</th>
                            <th>Cost / Lead</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(row, i) in reportData">
                            <tr>
                                <td>{{ getLabels(reportData)[i] }}</td>
                                <td>{{ row.calls ? row.calls.calls : 0 }}</td>
                                <td>{{ row.calls ? row.calls.leads : 0 }}</td>
                                <td>{{ row.calls ? (row.calls.leads / row.calls.calls * 100).toFixed(2) : 0 }}%</td>
                                <td>{{ row.forms.leads.toLocaleString() }}</td>
                                <td>{{ getTotalLeads(reportData)[i].toLocaleString('en') }}</td>
                                <td>${{ getTotalInvestment(reportData)[i].toLocaleString('en', numberFormat) }}</td>
                                <td>${{ getCostPerLead(reportData)[i].toLocaleString('en', numberFormat) }}</td>
                            </tr>

                            <tr class="comparison" v-if="comparison !== 'none' && comparisonData.length > 0">
                                <td>{{ getLabels(comparisonData)[i] }}</td>
                                <td>{{ comparisonData[i].calls ? comparisonData[i].calls.calls : 0 }}</td>
                                <td>{{ comparisonData[i].calls ? comparisonData[i].calls.leads : 0 }}</td>
                                <td>{{ comparisonData[i].calls ? (comparisonData[i].calls.leads / comparisonData[i].calls.calls * 100).toFixed(2) : 0 }}%</td>
                                <td>{{ comparisonData[i].forms.leads }}</td>
                                <td>{{ getTotalLeads(reportData)[i].toLocaleString('en') }}</td>
                                <td>${{ getTotalInvestment(reportData)[i].toLocaleString('en', numberFormat) }}</td>
                                <td>${{ getCostPerLead(reportData)[i].toLocaleString('en', numberFormat) }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>

            </div>
        </div>
    </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker'
import Chart from 'chart.js'
import colors from '@/data/colors'
import moment from 'moment'

import { mapActions, mapGetters } from 'vuex'

export default {
    name: 'ClientReview',
    components: {
        Datepicker
    },
    data() {
        return {
            charts: {
                costPerLead: false,
                totalLeads: false,
                totalInvestment: false
            },
            clientLists: [],
            clients: [],
            reportData: [],
            comparisonData: [],
            noReportData: false,
            numberFormat: {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            },
            comparison: 'none',
            selectMultiple: true,
            clientId: null,
            showCombinedTotals: true
        }
    },
    created() {
        let currentYear = moment().format('YYYY')
        if(this.startDate == null) {
            this.setStartDate(moment(currentYear + '-01-01').toDate())
        }
        if(this.endDate == null) {
            this.setEndDate(moment(currentYear + '-12-31').toDate())
        }

        if(this.$route.query.clientId) {
            this.clientId = this.$route.query.clientId
            this.selectMultiple = false
            this.updateData()
        }

        this.getClients()
        this.getLists()
    },
    watch: {
        comparison(value) {
            if(value !== 'none') {
                this.showCombinedTotals = true
            }
        }
    },
    computed: {
        ...mapGetters({
            startDate: 'getStartDate',
            endDate: 'getEndDate',
            listIds: 'getListIds',
            clientIds: 'getClientIds',
            management: 'getManagement',
            input: 'getInput'
        })
    },
    methods: {
        ...mapActions([
            'setStartDate',
            'setEndDate',
            'setManagement',
            'updateListIds',
            'updateClientIds',
            'addClientId',
            'selectClientId'
        ]),
        async getClients() {
            let response = await this.$http.get('/clients')
            this.clients = response.data
        },
        async getLists() {
            let response = await this.$http.get('/lists')
            this.clientLists = response.data
        },
        formatDate(date) {
            return moment(date).format('YYYY-MM-DD')
        },
        async updateData() {
            this.noReportData = false

            if(this.selectMultiple) {
                var response = await this.$http.post('/client-review', this.input)
            } else {
                let data = JSON.parse(JSON.stringify(this.input))
                data.clientIds = [this.clientId]
                var response = await this.$http.post('/client-review', data)
            }
            if(this.comparison !== 'none') {
                await this.getComparisonData()
            }

            this.reportData = response.data

            // Render Charts
            if(this.reportData.length > 0 ) {
                this.renderCostPerLeadChart()
                this.renderTotalLeadsChart()
                this.renderTotalInvestmentChart()
            } else {
                this.noReportData = true
            }
        },
        async getComparisonData() {
            let data = JSON.parse(JSON.stringify(this.input))
            if(this.comparison == 'period') {
                let diff = moment(data.endDate).diff(moment(data.startDate), 'months') + 1
                data.startDate = moment(data.startDate).subtract(diff, 'months').format('YYYY-MM-DD')
                data.endDate = moment(data.endDate).subtract(diff, 'months').format('YYYY-MM-DD')
            } else if(this.comparison == 'year') {
                data.startDate = moment(data.startDate).subtract(1, 'year').format('YYYY-MM-DD')
                data.endDate = moment(data.endDate).subtract(1, 'year').format('YYYY-MM-DD')
            }

            if(!this.selectMultiple) {
                data.clientIds = [this.clientId]
            }

            let response = await this.$http.post('/client-review', data)
            this.comparisonData = response.data
        },
        getLabels(data) {
            return data.map(row => {
                return moment(row.date).format('MMM YYYY')
            })
        },
        getCostPerLead(data) {
            return data.map(row => {
                if(this.input.management) {
                    return row.calculations.costPerLeadWithFee
                } else {
                    return row.calculations.costPerLead
                }
            })
        },
        getTotalLeads(data) {
            return data.map(row => {
                return row.calculations.totalLeads
            })
        },
        getTotalInvestment(data) {
            return data.map(row => {
                if(this.input.management) {
                    return row.calculations.investmentWithFee
                } else {
                    return row.forms.cost
                }
            })
        },
        renderCostPerLeadChart() {
            this.$nextTick(() => {
                if(this.charts.costPerLead) {
                    this.charts.costPerLead.destroy()
                }
                this.charts.costPerLead = new Chart(this.$refs.costPerLead, {
                    type: 'line',
                    data: {
                        labels: this.getLabels(this.reportData),
                        datasets: []
                    },
                    options: {
                        responsive: true,
                        title: {
                            display: false
                        },
                        legend: {
                            position: 'bottom',
                            labels: {
                                boxWidth: 15,
                                padding: 20
                            }
                        },
                        tooltips: {
                            xPadding: 10,
                            yPadding: 10,
                            mode: 'index',
                            bodySpacing: 7,
                            callbacks: {
                                label: function(item, chart) {
                                    var label = ' ' + chart.datasets[item.datasetIndex].label
                                    var value = item.yLabel.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    return label + ': $' + value
                                }
                            }
                        },
                        scales: {
                            yAxes: [{
                                ticks: {
                                    padding: 5,
                                    beginAtZero: true,
                                    callback: function(value) {
                                        return '$' + value.toLocaleString()
                                    }
                                }
                            }]
                        }
                    }
                })

                if(this.showCombinedTotals == true) {
                    // Combined Totals
                    this.charts.costPerLead.data.datasets = [{
                        label: 'Current',
                        data: this.getCostPerLead(this.reportData),
                        fill: false,
                        borderColor: colors[1],
                        backgroundColor: colors[1]
                    }]

                    if(this.comparison !== 'none') {
                        this.charts.costPerLead.data.datasets.push({
                            label: 'Previous',
                            data: this.getCostPerLead(this.comparisonData),
                            fill: false,
                            borderColor: colors[3],
                            backgroundColor: colors[3]
                        })
                    }
                } else {
                    // Split data by network (Google, Yahoo, Facebook)
                    // Zero out data
                    this.charts.costPerLead.data.datasets = []

                    // Google
                    let google = this.reportData.map(data => {
                        if(data.google) {
                            return (+data.google.leads > 0) ? (+data.google.cost / (+data.google.leads + +data.google.calls)) : 0
                        } else {
                            return 0
                        }
                    })
                    this.charts.costPerLead.data.datasets.push({
                        label: 'Google',
                        data: google,
                        fill: false,
                        borderColor: colors[1],
                        backgroundColor: colors[1]
                    })

                    // Yahoo / Bing
                    let yahoo = this.reportData.map(data => {
                        if(data.yahoo) {
                            return (+data.yahoo.leads > 0) ? (+data.yahoo.cost / (+data.yahoo.leads + +data.yahoo.calls)) : 0
                        } else {
                            return 0
                        }
                    })
                    this.charts.costPerLead.data.datasets.push({
                        label: 'Yahoo / Bing',
                        data: yahoo,
                        fill: false,
                        borderColor: colors[3],
                        backgroundColor: colors[3]
                    })

                    // Facebook
                    let facebook = this.reportData.map(data => {
                        if(data.facebook) {
                            return (+data.facebook.leads > 0) ? (+data.facebook.cost / (+data.facebook.leads + +data.facebook.calls)) : 0
                        } else {
                            return 0
                        }
                    })
                    this.charts.costPerLead.data.datasets.push({
                        label: 'Facebook',
                        data: facebook,
                        fill: false,
                        borderColor: colors[4],
                        backgroundColor: colors[4]
                    })
                }

                this.charts.costPerLead.update()
            })

        },
        renderTotalLeadsChart() {
            this.$nextTick(() => {
                if(this.charts.totalLeads) {
                    this.charts.totalLeads.destroy()
                }

                this.charts.totalLeads = new Chart(this.$refs.totalLeads, {
                    type: 'line',
                    data: {
                        labels: this.getLabels(this.reportData),
                        datasets: []
                    },
                    options: {
                        responsive: true,
                        title: {
                            display: false
                        },
                        legend: {
                            position: 'bottom',
                            labels: {
                                boxWidth: 15,
                                padding: 20
                            }
                        },
                        tooltips: {
                            xPadding: 10,
                            yPadding: 10,
                            mode: 'index',
                            bodySpacing: 7,
                            callbacks: {
                                label: function(item, chart) {
                                    var label = ' ' + chart.datasets[item.datasetIndex].label
                                    var value = item.yLabel.toLocaleString('en')
                                    return label + ': ' + value
                                }
                            }
                        },
                        scales: {
                            yAxes: [{
                                ticks: {
                                    padding: 5,
                                    beginAtZero: true,
                                    callback: function(value) {
                                        return value.toLocaleString('en')
                                    }
                                }
                            }]
                        }
                    }
                })

                if(this.showCombinedTotals == true) {
                    // Combined Totals
                    this.charts.totalLeads.data.datasets = [{
                        label: 'Current',
                        data: this.getTotalLeads(this.reportData),
                        fill: false,
                        borderColor: colors[1],
                        backgroundColor: colors[1]
                    }]

                    if(this.comparison !== 'none') {
                        this.charts.totalLeads.data.datasets.push({
                            label: 'Previous',
                            data: this.getTotalLeads(this.comparisonData),
                            fill: false,
                            borderColor: colors[3],
                            backgroundColor: colors[3]
                        })
                    }
                } else {
                    // Split data by network (Google, Yahoo, Facebook, Calls)
                    // Zero out data
                    this.charts.totalLeads.data.datasets = []

                    // Google
                    let google = this.reportData.map(data => {
                        if(data.google) {
                            return +data.google.leads
                        }  else {
                            return 0
                        }
                    })
                    this.charts.totalLeads.data.datasets.push({
                        label: 'Google',
                        data: google,
                        fill: false,
                        borderColor: colors[1],
                        backgroundColor: colors[1]
                    })

                    // Yahoo
                    let yahoo = this.reportData.map(data => {
                        if(data.yahoo) {
                            return +data.yahoo.leads
                        }  else {
                            return 0
                        }
                    })
                    this.charts.totalLeads.data.datasets.push({
                        label: 'Yahoo / Bing',
                        data: yahoo,
                        fill: false,
                        borderColor: colors[3],
                        backgroundColor: colors[3]
                    })

                    // Facebook
                    let facebook = this.reportData.map(data => {
                        if(data.facebook) {
                            return +data.facebook.leads
                        }  else {
                            return 0
                        }
                    })
                    this.charts.totalLeads.data.datasets.push({
                        label: 'Facebook',
                        data: facebook,
                        fill: false,
                        borderColor: colors[4],
                        backgroundColor: colors[4]
                    })


                    // Google
                    let calls = this.reportData.map(data => {
                        if(data.calls) {
                            return +data.calls.leads
                        }  else {
                            return 0
                        }
                    })
                    this.charts.totalLeads.data.datasets.push({
                        label: 'Calls',
                        data: calls,
                        fill: false,
                        borderColor: colors[0],
                        backgroundColor: colors[0]
                    })

                }

                this.charts.totalLeads.update()
            })

        },
        renderTotalInvestmentChart() {
            this.$nextTick(() => {
                if(this.charts.totalInvestment) {
                    this.charts.totalInvestment.destroy()
                }

                this.charts.totalInvestment = new Chart(this.$refs.totalInvestment, {
                    type: 'line',
                    data: {
                        labels: this.getLabels(this.reportData),
                        datasets: []
                    },
                    options: {
                        responsive: true,
                        title: {
                            display: false
                        },
                        legend: {
                            position: 'bottom',
                            labels: {
                                boxWidth: 15,
                                padding: 20
                            }
                        },
                        tooltips: {
                            xPadding: 10,
                            yPadding: 10,
                            mode: 'index',
                            bodySpacing: 7,
                            callbacks: {
                                label: function(item, chart) {
                                    var label = ' ' + chart.datasets[item.datasetIndex].label
                                    var value = item.yLabel.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    return label + ': $' + value
                                }
                            }
                        },
                        scales: {
                            yAxes: [{
                                ticks: {
                                    padding: 5,
                                    beginAtZero: true,
                                    callback: function(value) {
                                        return '$' + value.toLocaleString('en')
                                    }
                                }
                            }]
                        }
                    }
                })

                if(this.showCombinedTotals == true) {
                    this.charts.totalInvestment.data.datasets = [{
                        label: 'Current',
                        data: this.getTotalInvestment(this.reportData),
                        fill: false,
                        borderColor: colors[1],
                        backgroundColor: colors[1]
                    }]

                    if(this.comparison !== 'none') {
                        this.charts.totalInvestment.data.datasets.push({
                            label: 'Previous',
                            data: this.getTotalInvestment(this.comparisonData),
                            fill: false,
                            borderColor: colors[3],
                            backgroundColor: colors[3]
                        })
                    }
                } else {
                    // Split data by network (Google, Yahoo, Facebook, Calls)
                    // Zero out data
                    this.charts.totalInvestment.data.datasets = []

                    // Google
                    let google = this.reportData.map(data => {
                        if(data.google) {
                            return +data.google.cost
                        }  else {
                            return 0
                        }
                    })
                    this.charts.totalInvestment.data.datasets.push({
                        label: 'Google',
                        data: google,
                        fill: false,
                        borderColor: colors[1],
                        backgroundColor: colors[1]
                    })

                    // Yahoo
                    let yahoo = this.reportData.map(data => {
                        if(data.yahoo) {
                            return +data.yahoo.cost
                        }  else {
                            return 0
                        }
                    })
                    this.charts.totalInvestment.data.datasets.push({
                        label: 'Yahoo / Bing',
                        data: yahoo,
                        fill: false,
                        borderColor: colors[3],
                        backgroundColor: colors[3]
                    })

                    // Facebook
                    let facebook = this.reportData.map(data => {
                        if(data.facebook) {
                            return +data.facebook.cost
                        }  else {
                            return 0
                        }
                    })
                    this.charts.totalInvestment.data.datasets.push({
                        label: 'Facebook',
                        data: facebook,
                        fill: false,
                        borderColor: colors[4],
                        backgroundColor: colors[4]
                    })
                }

                this.charts.totalInvestment.update()
            })
        }
    }


}
</script>

<style lang="scss">
#client-review {
    #settings {
        background: lighten($light-gray, 3%);
        padding: 15px 30px;
        margin-bottom: 45px;

        .col {
            margin-bottom: 0;
        }

        button {
            margin: 0;
        }
    }

    #comparison {
        width: 50%;
    }

    .no-data-message {
        padding: 30px;
        text-align: center;
    }

    .client-selection {
        padding: 5px;
        height: 225px;
        overflow-y: scroll;
        border: 1px solid $medium-gray;
        border-radius: 5px;
        background: $white;
    }

    .client-list-edit {
        display: inline-block;
        margin-left: 5px;
        font-size: 12px;
    }

    .chart-title {
        font-size: 18px;
        font-weight: 700;
        margin: 10px  45px;
    }

    table {
        margin-top: 60px;

        td,
        th {
            padding: 5px;
            text-align: right;

            &:first-of-type {
                text-align: center;
            }
        }

        .comparison {
            td {
                color: $red;
            }
        }
    }
}
</style>
